import { type Category, type CategoryKind, type Wallet } from '@fin/shared';
import { useState, type ReactNode } from 'react';
import { authStore } from '../../lib/api.ts';
import { categoryTree, orderWallets, useCategories, useWallets } from '../../lib/data.ts';

import { kindLabels, money } from '../../lib/format.ts';
import { categoryColor, categoryIcon, walletIcon } from '../../lib/icons.tsx';



import { Icon, Input, Modal, Segmented, Tile, cx, useIsMobile } from '../../components/ui.tsx';

export function PickerShell({ title, onClose, search, onSearch, head, children }: {
  title: string; onClose: () => void; search: string; onSearch: (v: string) => void; head?: ReactNode; children: ReactNode;
}) {
  const mobile = useIsMobile();
  const field = <Input type="search" value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Поиск" aria-label="Поиск" autoFocus={!mobile} />;
  return (
    <Modal full title={title} onClose={onClose} className="modal-picker" bodyClass="picker-body"
      head={head || !mobile ? <div className="picker-head">{head}{!mobile ? <div className="picker-search">{field}</div> : null}</div> : undefined}
      dock={mobile ? <div className="picker-dock"><Icon name="search" size={18} />{field}</div> : undefined}>
      {children}
    </Modal>
  );
}

export type PickerKindTab = 'expense' | 'income' | 'debt';

export const kindTabs: { value: PickerKindTab; label: string }[] = [
  { value: 'expense', label: 'Расход' }, { value: 'income', label: 'Доход' }, { value: 'debt', label: 'Долг/Займ' },
];

export const debtKindsAll: CategoryKind[] = ['debt', 'repayment', 'loan', 'debt_collection'];

export function CategoryPicker({ kind, value, onPick, onClose, allLabel, onAll, onTab }: {
  kind?: CategoryKind; value: number | null; onPick: (id: number) => void; onClose: () => void; allLabel?: string; onAll?: () => void;
  onTab?: (tab: PickerKindTab) => void;
}) {
  const cats = useCategories();
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const tab: PickerKindTab | null = !kind ? null : kind === 'expense' || kind === 'income' ? kind : 'debt';
  // the debt tab lists all four debt kinds, each under its own heading
  const kinds: CategoryKind[] = !kind ? ['expense', 'income', 'debt', 'repayment', 'loan', 'debt_collection', 'transfer']
    : tab === 'debt' && onTab ? debtKindsAll : [kind];
  const groups = kinds.map((k) => {
    const tree = categoryTree((cats.data ?? []).filter((c) => c.kind === k && (!c.archived || c.id === value)));
    const visible = q
      ? tree.map((n) => ({ cat: n.cat, children: n.cat.name.toLowerCase().includes(q) ? n.children : n.children.filter((c) => c.name.toLowerCase().includes(q)) }))
        .filter((n) => n.cat.name.toLowerCase().includes(q) || n.children.length)
      : tree;
    return { kind: k, nodes: visible };
  }).filter((g) => g.nodes.length);
  const row = (c: Category, child: boolean) => (
    <button key={c.id} type="button" className={cx('picker-row', child && 'child', c.id === value && 'on')} onClick={() => onPick(c.id)} aria-pressed={c.id === value}>
      <Tile icon={categoryIcon(c)} color={categoryColor(c)} image={c.iconImage} />
      <span className="picker-row-text"><span>{c.name}</span></span>
      {c.id === value ? <Icon name="check" size={18} /> : <span />}
    </button>
  );
  const head = onTab && tab ? (
    <Segmented wide label="Тип категорий" value={tab} onChange={(t) => onTab(t)} options={kindTabs} />
  ) : undefined;
  return (
    <PickerShell title="Выберите категорию" onClose={onClose} search={search} onSearch={setSearch} head={head}>
      {onAll && !q ? (
        <div className="pick-group">
          <button type="button" className={cx('picker-row', value === null && 'on')} onClick={onAll}>
            <Tile icon="folder" color="#8F8A7E" /><span className="picker-row-text"><span>{allLabel ?? 'Все категории'}</span></span>{value === null ? <Icon name="check" size={18} /> : <span />}
          </button>
        </div>
      ) : null}
      {groups.map((g) => (
        <div key={g.kind} className="pick-kind">
          {kinds.length > 1 ? <div className="picker-group">{kindLabels[g.kind]}</div> : null}
          {g.nodes.map((n) => (
            <div key={n.cat.id} className={cx('pick-group', n.children.length > 0 && 'has-children')}>
              {row(n.cat, false)}
              {n.children.length ? <div className="pick-children">{n.children.map((c) => row(c, true))}</div> : null}
            </div>
          ))}
        </div>
      ))}
      {!groups.length ? <p className="picker-empty">{cats.data ? 'Ничего не найдено. Категории добавляются в настройках.' : 'Загружаю…'}</p> : null}
    </PickerShell>
  );
}

export function WalletPicker({ title, value, exclude, onPick, onClose, allLabel, onAll }: {
  title: string; value: number | null; exclude?: number; onPick: (id: number) => void; onClose: () => void; allLabel?: string; onAll?: () => void;
}) {
  const wallets = useWallets();
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const all = orderWallets(wallets.data ?? [], authStore.get().user?.walletTypeOrder).filter((w) => !w.archived && w.id !== exclude && (!q || w.name.toLowerCase().includes(q)));
  const shown = all.filter((w) => !w.hidden);
  const hidden = all.filter((w) => w.hidden);
  const row = (w: Wallet) => (
    <button key={w.id} type="button" className={cx('picker-row', w.id === value && 'on')} onClick={() => onPick(w.id)} aria-pressed={w.id === value}>
      <Tile icon={walletIcon(w)} color={w.color} />
      <span className="picker-row-text"><span>{w.name}</span></span>
      <span className="money">{money(w.balance, w.currency)}</span>
    </button>
  );
  return (
    <PickerShell title={title} onClose={onClose} search={search} onSearch={setSearch}>
      {onAll && !q ? (
        <button type="button" className={cx('picker-row', value === null && 'on')} onClick={onAll}>
          <Tile icon="wallet" color="#8E97A7" /><span className="picker-row-text"><span>{allLabel ?? 'Все кошельки'}</span></span>{value === null ? <Icon name="check" size={18} /> : <span />}
        </button>
      ) : null}
      {shown.map(row)}
      {hidden.length ? (
        <details className="picker-hidden" open={!!q}>
          <summary><Icon name="down" size={16} /> Скрытые кошельки ({hidden.length})</summary>
          {hidden.map(row)}
        </details>
      ) : null}
      {!all.length ? <p className="picker-empty">{wallets.data ? 'Ничего не найдено.' : 'Загружаю…'}</p> : null}
    </PickerShell>
  );
}
